import React, { useEffect, useState } from "react";
import { useApp } from "@/contexts/AppContext";
import { api } from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/common/Bits";
import { Check, Sparkles, Zap, Crown, Building2, Coins, Gift, X, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const PLAN_ICONS = { free: Zap, pro: Sparkles, boutique: Building2, enterprise: Crown };

function PlanCard({ plan, current, onPick, busy }) {
  const Icon = PLAN_ICONS[plan.id] || Sparkles;
  const isPro = plan.id === "pro";
  const disabled = plan.available === false || current || !plan.price_eur;
  return (
    <div
      data-testid={`plan-card-${plan.id}`}
      className={`relative surface-card surface-card-hover p-6 flex flex-col ${isPro ? "border-[hsl(var(--gold)/0.5)] shadow-[0_0_40px_-12px_hsl(var(--gold)/0.5)]" : ""}`}
    >
      {isPro && <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-mono uppercase tracking-widest gold-text bg-card px-2 border gold-border rounded">Δημοφιλές</div>}
      <div className="flex items-center gap-2 mb-3">
        <div className={`size-8 rounded-md flex items-center justify-center ${isPro ? "bg-[hsl(var(--gold)/0.15)] gold-border border" : "bg-muted"}`}>
          <Icon className={`size-4 ${isPro ? "gold-text" : ""}`} strokeWidth={1.5}/>
        </div>
        <h3 className="font-heading font-semibold text-[15px]">{plan.name_el}</h3>
        {current && <StatusBadge tone="gold">Ενεργό</StatusBadge>}
      </div>
      <div className="mb-5">
        <span className="stat-num text-4xl">{plan.price_eur ? `€${plan.price_eur}` : "Custom"}</span>
        {plan.price_eur ? <span className="text-muted-foreground text-[12px] font-mono"> / μήνα</span> : null}
      </div>
      <ul className="space-y-2 mb-6 flex-1">
        <Feat>{plan.ai_calls_per_month === -1 ? "Απεριόριστα" : `${plan.ai_calls_per_month}`} AI calls / μήνα</Feat>
        <Feat>{plan.max_cases === -1 ? "Απεριόριστες" : plan.max_cases} ενεργές υποθέσεις</Feat>
        <Feat>{plan.max_seats === -1 ? "Απεριόριστες" : plan.max_seats} {plan.max_seats === 1 ? "θέση" : "θέσεις"}</Feat>
        {plan.features.includes("premium_exports") && <Feat>Premium exports (PDF/DOCX)</Feat>}
        {plan.features.includes("ocr") && <Feat>OCR & semantic search</Feat>}
        {plan.features.includes("multi_team") && <Feat>Multi-team workspaces</Feat>}
        {plan.features.includes("audit_log") && <Feat>Audit log</Feat>}
        {plan.features.includes("collaboration") && <Feat>Collaboration</Feat>}
        {plan.features.includes("sso") && <Feat>SSO / SAML</Feat>}
      </ul>
      <button
        data-testid={`plan-buy-${plan.id}`}
        disabled={disabled || busy}
        onClick={() => onPick("plan", plan.id)}
        className={`w-full px-4 py-2.5 rounded-md font-medium text-[13px] transition-all
          ${disabled
            ? "bg-muted text-muted-foreground cursor-not-allowed"
            : isPro
              ? "bg-[hsl(var(--gold))] text-[hsl(var(--background))] hover:opacity-90"
              : "border border-[hsl(var(--gold)/0.4)] gold-text hover:bg-[hsl(var(--gold)/0.08)]"}`}
      >
        {current ? "Ενεργό πλάνο" : plan.available === false ? "Προσεχώς" : plan.price_eur ? `Αναβάθμιση σε ${plan.name_el}` : "Επικοινωνία"}
      </button>
    </div>
  );
}

function Feat({ children }) {
  return (
    <li className="flex items-start gap-2 text-[12.5px]">
      <Check className="size-3.5 gold-text shrink-0 mt-0.5" strokeWidth={2}/>
      <span>{children}</span>
    </li>
  );
}

export default function Billing() {
  const { t, currentCase } = useApp();
  const [plans, setPlans] = useState([]);
  const [topups, setTopups] = useState([]);
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);
  const [referral, setReferral] = useState(null);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    api.get("/billing/plans").then(r => { setPlans(r.data.plans); setTopups(r.data.topups); });
    api.get("/billing/subscription").then(r => setSub(r.data));
    api.get("/referrals/me").then(r => setReferral(r.data)).catch(() => {});
    api.get("/billing/invoices").then(r => setInvoices(r.data)).catch(() => {});
  }, []);

  const buy = async (kind, key) => {
    setBusy(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/billing/checkout", { kind, key, origin_url: origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Σφάλμα Stripe");
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Επιβεβαίωση ακύρωσης συνδρομής στο τέλος της περιόδου;")) return;
    try {
      await api.post("/billing/cancel-subscription");
      const { data } = await api.get("/billing/subscription");
      setSub(data);
      toast.success("Η συνδρομή θα ακυρωθεί στο τέλος της περιόδου");
    } catch (e) { toast.error("Σφάλμα"); }
  };

  const copyReferral = async () => {
    if (!referral?.link) return;
    await navigator.clipboard.writeText(referral.link);
    toast.success("Αντιγράφηκε ο σύνδεσμος");
  };

  return (
    <div data-testid="billing-page">
      <PageHeader
        title="Συνδρομή & Χρεώσεις"
        subtitle="Πλάνα συνδρομής, AI credits και διαχείριση χρέωσης."
      />

      {sub && (
        <div className="surface-card p-6 mb-8 grid grid-cols-1 md:grid-cols-4 gap-6" data-testid="current-plan-card">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">Τρέχον Πλάνο</div>
            <div className="font-heading font-semibold text-lg gold-text mt-1">{sub.plan.name_el}</div>
            {sub.is_paid && sub.subscription.ends_at && (
              <div className="text-[11px] text-muted-foreground font-mono mt-1">
                Λήξη: {new Date(sub.subscription.ends_at).toLocaleDateString("el-GR")}
              </div>
            )}
            {sub.subscription.cancel_at_period_end && (
              <div className="text-[11px] text-amber-400 font-mono mt-1">Ακυρώνεται στη λήξη</div>
            )}
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">AI Calls</div>
            <div className="stat-num text-2xl mt-1" data-testid="remaining-ai-calls">
              {sub.remaining_ai_calls === -1 ? "∞" : sub.remaining_ai_calls}
            </div>
            <div className="text-[11px] text-muted-foreground font-mono">διαθέσιμες</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">Bonus credits</div>
            <div className="stat-num text-2xl mt-1">{sub.subscription.ai_credit_balance || 0}</div>
            <div className="text-[11px] text-muted-foreground font-mono">από top-ups</div>
          </div>
          <div className="flex flex-col items-end justify-between gap-2">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">Χρήση μήνα</div>
              <div className="stat-num text-2xl mt-1 text-right">{sub.subscription.ai_calls_used_this_period}</div>
            </div>
            {sub.is_paid && !sub.subscription.cancel_at_period_end && (
              <button data-testid="cancel-sub-btn" onClick={cancel}
                className="text-[11px] text-muted-foreground hover:text-red-400 underline-offset-2 hover:underline">
                Ακύρωση συνδρομής
              </button>
            )}
          </div>
        </div>
      )}

      {/* Referral program */}
      {referral && (
        <div className="surface-card p-6 mb-8 relative overflow-hidden" data-testid="referral-card">
          <div className="absolute -right-10 -top-10 size-40 rounded-full bg-[hsl(var(--gold)/0.08)] blur-2xl pointer-events-none"/>
          <div className="flex items-start gap-4 relative">
            <div className="size-12 rounded-lg flex items-center justify-center bg-[hsl(var(--gold)/0.15)] gold-border border shrink-0">
              <Gift className="size-5 gold-text" strokeWidth={1.5}/>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading font-semibold text-[15px] mb-1">Πρόγραμμα Συστάσεων</h3>
              <p className="text-[12.5px] text-muted-foreground mb-3">
                Καλέστε άλλους δικηγόρους. Κάθε επιτυχημένη εγγραφή σας δίνει <span className="gold-text font-semibold">30 ημέρες δωρεάν Pro</span>.
              </p>
              <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                <code className="flex-1 text-[12px] font-mono bg-muted/40 rounded px-3 py-2 border border-border/60 truncate">{referral.link}</code>
                <button data-testid="copy-referral-btn" onClick={copyReferral}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-[hsl(var(--gold)/0.4)] gold-text hover:bg-[hsl(var(--gold)/0.08)] text-[12px] font-medium">
                  <Copy className="size-3.5"/>Αντιγραφή
                </button>
              </div>
              <div className="flex gap-6 mt-3 text-[12px]">
                <span><span className="gold-text font-semibold">{referral.referrals}</span> εγγραφές</span>
                <span><span className="gold-text font-semibold">{referral.successful}</span> επιβραβευμένες</span>
                <span><span className="gold-text font-semibold">{referral.successful * 30}</span> ημέρες κερδισμένες</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <h2 className="font-heading font-semibold text-lg mb-4">Πλάνα Συνδρομής</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-12">
        {plans.map(p => (
          <PlanCard key={p.id} plan={p} current={sub?.plan?.id === p.id} onPick={buy} busy={busy}/>
        ))}
      </div>

      <h2 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
        <Coins className="size-5 gold-text"/>AI Credit Top-ups
      </h2>
      <p className="text-muted-foreground text-[13px] mb-4">Μεμονωμένα πακέτα AI credits για ad-hoc χρήση. Δεν λήγουν.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
        {topups.map(pkg => (
          <div key={pkg.id} data-testid={`topup-card-${pkg.id}`}
            className="surface-card surface-card-hover p-6">
            <div className="font-heading font-semibold text-[14px] mb-2">{pkg.name_el}</div>
            <div className="flex items-baseline gap-2 mb-4">
              <span className="stat-num text-3xl">€{pkg.price_eur}</span>
              <span className="text-[12px] text-muted-foreground font-mono">/ εφάπαξ</span>
            </div>
            <div className="text-[13px] mb-4">
              <span className="gold-text font-semibold">{pkg.credits}</span> AI credits
              <span className="text-muted-foreground"> (€{(pkg.price_eur / pkg.credits).toFixed(2)} / call)</span>
            </div>
            <button
              data-testid={`topup-buy-${pkg.id}`}
              disabled={busy}
              onClick={() => buy("topup", pkg.id)}
              className="w-full px-4 py-2 rounded-md border border-[hsl(var(--gold)/0.4)] gold-text hover:bg-[hsl(var(--gold)/0.08)] text-[13px] font-medium"
            >
              Αγορά
            </button>
          </div>
        ))}
      </div>

      {invoices.length > 0 && (
        <div data-testid="invoices-section">
          <h2 className="font-heading font-semibold text-lg mb-4">Τιμολόγια</h2>
          <div className="surface-card overflow-hidden">
            <table className="w-full text-[13px]">
              <thead className="bg-muted/30 border-b border-border/70">
                <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground font-mono">
                  <th className="px-5 py-3">Ημερομηνία</th>
                  <th className="px-5 py-3">Περιγραφή</th>
                  <th className="px-5 py-3 text-right">Ποσό</th>
                  <th className="px-5 py-3">Κατάσταση</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(inv => (
                  <tr key={inv.session_id} className="border-b border-border/40 last:border-0">
                    <td className="px-5 py-3 font-mono text-[11.5px] text-muted-foreground">
                      {new Date(inv.created_at).toLocaleDateString("el-GR")}
                    </td>
                    <td className="px-5 py-3">{inv.kind === "plan" ? "Συνδρομή" : "AI Credits"} — {inv.key}</td>
                    <td className="px-5 py-3 text-right font-mono">€{inv.amount?.toFixed(2)}</td>
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10.5px] font-mono uppercase tracking-wider border bg-emerald-500/15 text-emerald-400 border-emerald-500/30">PAID</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
